import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useAuth } from "../../../auth-context/auth.context";
import AuthApi from "../../../api/auth";
import DefaultAuth from "layouts/auth/Default";

function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(undefined);
  const [buttonText, setButtonText] = useState("Sign in");
  const [show, setShow] = useState(false);

  const history = useHistory();
  const { user, setUser } = useAuth();

  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const brandStars = useColorModeValue("brand.500", "brand.400");

  // ✅ Clear any previous login data when SignIn page mounts
  React.useEffect(() => {
    // console.log("🧹 Clearing old local storage data...");
    // localStorage.clear(); // removes everything from localStorage
    // sessionStorage.clear(); // optional: also clear session storage if used
    console.log("🧹 Clearing session data...");
    localStorage.removeItem("user"); // Only remove user, leave persistent settings/flags
    sessionStorage.removeItem("user");
  }, []);


  const handleClick = () => setShow(!show);

  const login = async (event) => {


    if (event) event.preventDefault();

    // If already logged in, redirect immediately
    if (user && user.token) {
      return history.push("/admin/dashboards");
    }

    // Validate inputs
    if (email.trim() === "") {
      setError("You must enter your email.");
      return;
    }
    if (password.trim() === "") {
      setError("You must enter your password");
      return;
    }

    setButtonText("Signing in");
    setError(undefined);

    try {
      const response = await AuthApi.Login({
        email,
        password,
      });

      // If backend reports failure
      if (response.data && response.data.success === false) {
        setButtonText("Sign in");
        setError(response.data.message || "Invalid login");
        return;
      }

      // Handle successful login
      // console.log("Login successful:", response.data);
      return setProfile(response);
    } catch (err) {
      console.error(err);
      setButtonText("Sign in");
      setError(err.message || "There has been an error.");
    }
  };

  const setProfile = async (response) => {
    // Backend currently only sends message and success
    // Adjust this when backend sends user and token
    // For now just proceed with redirect on success

    if (response.data.success) {
      console.log("Login successful:", response.data);
      const userObject = {
        // Placeholder minimal user, expand when backend sends user data
        email,
        token: "DUMMY_TOKEN", // No token yet from backend, add later when available
        name: response.data.employeeFullName || "User",
        id: response.data.employeeId || "12345",
        branchId: response.data.branchId || "default-branch",
        branchName: response.data.branchName || "Default Branch",
        departmentId: response.data.departmentId || "Default department",
        companyCode: response.data.companyCode || "default-company",
        finyearCode: response.data.finyearCode || "default-finyear",
        photo: response.data.photo || "",
        companyName: response.data.companyName || "Default Company",
        // loginflag: response.data.Flag || "",
        loginflag: response.data.flagValue || "",
      };

      // Check if this specific user has already skipped password change locally
      const isSkipped = localStorage.getItem(`skipped_pwd_${userObject.id}`);
      if (isSkipped === "true") {
        userObject.loginflag = "N"; // Override "Y" if they skipped before
      }
      setUser(userObject);
      localStorage.setItem("user", JSON.stringify(userObject));


      // Redirect to  admin dashboard
      // history.push("/admin/dashboards");

      // Conditional redirect based on loginflag
      if (userObject.loginflag === "Y") {
        history.push("/admin/change_password");
        // history.push("/admin/dashboards");
      } else {
        // history.push("/admin/change_password");
        history.push("/admin/dashboards");
      }
    } else {
      setError(response.data.message || "Login failed");
    }
    setButtonText("Sign in");
  };
 
  return (
    <DefaultAuth>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "0px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Sign In
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Employee Portal – Manage your work, attendance, and applications all
            in one place.
          </Text>
        </Box>

        <Flex
          zIndex="2"
          direction="column"
          w={{ base: "100%", md: "420px" }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: "auto", lg: "unset" }}
          me="auto"
          mb={{ base: "20px", md: "auto" }}
        >
          <h4
            style={{
              fontSize: ".9em",
              color: "red",
              textAlign: "center",
              fontWeight: 400,
              transition: ".2s all",
            }}
          >
            {error}
          </h4>

          <FormControl>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Email&nbsp;<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              id="email"                  // ✅ unique ID
              name="email"                // ✅ unique name
              isRequired
              variant="auth"
              fontSize="sm"
              ms={{ base: "0px", md: "0px" }}
              type="email"
              placeholder="email@example.com"
              mb="24px"
              value={email}
              fontWeight="500"
              size="lg"
              onChange={(e) => {
                setEmail(e.target.value);
                setError(undefined);
              }}
            />

            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              display="flex"
            >
              Password&nbsp;<Text color={brandStars}>*</Text>
            </FormLabel>

            <InputGroup size="md">
              <Input
                id="password"              // ✅ unique ID
                name="password"            // ✅ unique name
                isRequired
                fontSize="sm"
                placeholder="Min. 8 characters"
                mb="24px"
                size="lg"
                value={password}
                type={show ? "text" : "password"}
                variant="auth"
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(undefined);
                }}
              />
              <InputRightElement display="flex" alignItems="center" mt="4px">
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: "pointer" }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>

            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              onClick={login}
              isLoading={buttonText === "Signing in"}
              loadingText="Signing in"
            >
              {buttonText}
            </Button>
          </FormControl>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
